export interface AuthSession {
  accessToken: string;
  refreshToken: string;
  accessTokenExpiresInSeconds: number;
  userId: number;
  fullName: string;
  email: string;
  roles: string[];
  permissions: string[];
  organizationId: number | null;
}

const STORAGE_KEY = 'eventflow.session';

export function setSession(session: AuthSession): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

export function getSession(): AuthSession | null {
  if (typeof window === 'undefined') return null;
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthSession;
  } catch {
    return null;
  }
}

export function clearSession(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(STORAGE_KEY);
}

export function getAccessToken(): string | null {
  return getSession()?.accessToken ?? null;
}

export function getRefreshToken(): string | null {
  return getSession()?.refreshToken ?? null;
}

export function hasPermission(code: string): boolean {
  return getSession()?.permissions.includes(code) ?? false;
}

/**
 * Landing page after login when a user holds multiple roles: super admin,
 * admin and organizer duties take priority over the plain customer view.
 * ADMIN and ORGANIZER both land on the (now real) Organizer Dashboard - their
 * event performance overview - rather than Organization Settings/Event
 * Management directly. SUPER_ADMIN still lands on Organization Settings
 * (platform-wide admin, not one organization's event performance).
 * platform-admin-dashboard is still unlinked/unused entirely.
 * A customer lands on Browse Events (not their bookings list) - discovering
 * events to book is the more useful landing point right after login/signup.
 */
export function getDashboardPathForRoles(roles: string[]): string {
  if (roles.includes('SUPER_ADMIN')) return '/admin-dashboard';
  if (roles.includes('ADMIN')) return '/organizer-dashboard';
  if (roles.includes('ORGANIZER')) return '/organizer-dashboard';
  if (roles.includes('TICKET_SCANNER')) return '/qr-check-in-screen';
  return '/browse-events';
}
